import { Component, OnInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StartSlotPickerComponent, SlotSelectionEvent } from './start-slot-picker/start-slot-picker.component';
import { finalize, Subscription, Subject } from 'rxjs';
import { switchMap, filter } from 'rxjs/operators';
import {
  CourtAvailabilityService,
  Court,
  BulkAvailableSlotsResponseDto,
  DurationOption,
  AggregatedSlotDto,
  CourseSessionDto,
  DayAggregatedSlotsDto,
  isBlockedCourtState,
} from '../../service/court-availability-service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  DurationPickerComponent,
  CourtRowModel,
  CourtDurationSelectionEvent,
} from './duration-picker/duration-picker.component';
import { CategorySelectorComponent } from "./category-selector/category-selector.component";
import { SimplifiedCategorySelectorComponent } from "./simplified-category-selector/simplified-category-selector.component";
import { Category } from '../../models/category.model';
import { SlotHoldService } from '../../service/slot-hold.service';
import { BookingReserveComponent } from '../booking-reserve/booking-reserve.component';
import { BookingModalComponent } from '../booking-modal/booking-modal.component';
import { CoursePickerComponent } from './course-picker/course-picker.component';
import { CourseEnrollmentModalComponent } from '../../features/courses/components/course-enrollment-modal/course-enrollment-modal.component';
import { TimeUtilsService } from '../../service/time-utils.service';
import { ModernCalendarComponent } from '../../shared/modern-calendar/modern-calendar.component';
import { DurationFilterMultiComponent } from './duration-filter-multi/duration-filter-multi.component';
import { CourtTimeGridComponent } from './court-grid-view/court-time-grid/court-time-grid.component';
import { CourtGridDataService } from './court-grid-view/court-grid-data.service';
import { ViewToggleComponent } from './court-grid-view/view-toggle/view-toggle.component';
import { GridSelectionPanelComponent } from './court-grid-view/grid-selection-panel/grid-selection-panel.component';
import {
  BookingViewMode,
  CourtGridSelectionEvent,
  CourtDurationOption,
  DayGridModel,
  isDayViewMode,
  isWeekViewMode,
} from './court-grid-view/court-grid.model';
import { CourtLanesGridComponent } from './court-grid-view/court-lanes-grid/court-lanes-grid.component';
import { ToastService } from '../../shared/services/toast.service';
import { ErrorHandlingService } from '../../shared/services/error-handling.service';
import { OrganizationService } from '../../core/services/organization.service';
import { AuthService } from '../../auth/components/auth.service';
import { CoursesService } from '../../features/courses/services/courses.service';
import { VenueTimezoneService } from '../../service/venue-timezone.service';
import { formatLocalISODate, mondayOf, parseLocalDate } from '../../shared/utils/date-format.util';

@Component({
  selector: 'app-booking-container',
  imports: [
    CommonModule,
    FormsModule,
    StartSlotPickerComponent,
    DurationPickerComponent,
    CategorySelectorComponent,
    SimplifiedCategorySelectorComponent,
    BookingReserveComponent,
    BookingModalComponent,
    CoursePickerComponent,
    CourseEnrollmentModalComponent,
    DurationFilterMultiComponent,
    ModernCalendarComponent,
    CourtTimeGridComponent,
    CourtLanesGridComponent,
    ViewToggleComponent,
    GridSelectionPanelComponent,
],
  templateUrl: './booking-container.component.html',
  styleUrl: './booking-container.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class BookingContainerComponent implements OnInit, OnDestroy {
  // State management
  selectedDate: Date = new Date();
  selectedCategory: Category | null = null;
  selectedDurationOption: DurationOption | null = null; // used for the duration option component
  selectedDurationInMinutes: number | null = null; // tracking the currently selected duration from user
  selectedSlot: AggregatedSlotDto | null = null;
  selectedSlotId: string | null = null;
  durationSelectionOptions: number[] = []; // Options for duration selection component, e.g. [30, 60, 90]

  // Available data
  availableCourts: Court[] = [];
  availableIntervals: number[] = [];
  availableSlots: BulkAvailableSlotsResponseDto = { courts: [] };
  aggregatedSlots: AggregatedSlotDto[] = [];

  /* duration filter state */
  filteredIntervals: number[] = []; // Currently selected intervals by user
  selectedIntervalFilters: boolean[] = []; // Selected state for each filter option
  allIntervalFiltersSelected = true; // Whether "All" is selected
  
  // Loading states
  loading = false;
  loadingIntervals = false;
  loadingSlots = false;
  
  // Settings
  preserveDurationBetweenDays = true;

  // Add this property
  showBookingModal: boolean = false;

  // Course picker state (inline component - both course picker and duration picker shown simultaneously when applicable)
  availableCourseSessions: CourseSessionDto[] = [];

  // Course enrollment modal state
  showEnrollmentModal: boolean = false;
  enrollmentModalSession: CourseSessionDto | null = null;


  // Dates with available course sessions (for calendar indicators)
  datesWithCourseSessions: Set<string> = new Set();

  // Track the currently displayed calendar month for course dates fetching
  displayedCalendarMonth: { year: number; month: number } = {
    year: new Date().getFullYear(),
    month: new Date().getMonth()
  };

  // Filter to show only available slots (enabled by default)
  showAvailableOnly: boolean = true;

  // View mode toggle (simplified vs day/week grid)
  viewMode: BookingViewMode = 'simplified';
  intervalMinutes: number = 30;
  /**
   * Court the user's selection is bound to — set by the simplified per-court
   * duration picker AND by the day/week grid. onReserveClicked() reads it so
   * the hold request always carries the explicitly picked court id.
   */
  selectedCourt: { id: string; name: string } | null = null;
  gridAvailableDurations: CourtDurationOption[] = [];

  // Simplified-view selection state ("Einfache Ansicht", 2a integration):
  /** What the reserve CTA acts on — null renders the "Keine Auswahl" state. */
  bookingMode: 'court' | 'course' | null = null;
  selectedCourseSession: CourseSessionDto | null = null;
  /** One row per court of the selected slot, with per-court duration feasibility. */
  courtRows: CourtRowModel[] = [];

  // Day/week grid data — pivoted via CourtGridDataService.buildWeekGrid() into
  // the shape app-court-time-grid consumes. gridDays is rebuilt whenever
  // aggregatedSlots (day mode) or weekDaySlots (week mode) changes.
  gridDays: DayGridModel[] = [];
  /** Raw per-day slots for Wochenansicht, from the weekly aggregated-slots endpoint. */
  weekDaySlots: DayAggregatedSlotsDto[] = [];
  /** Monday of the week currently displayed in Wochenansicht. */
  weekStartDate: Date | null = null;
  /** Full-width layout toggle — only meaningful in day/week grid mode. */
  expanded = false;

  // Add a collection to track subscriptions
  private subscriptions = new Subscription();

  // Subject for course dates requests - enables automatic cancellation of stale requests via switchMap
  private courseDatesRequest$ = new Subject<{ year: number; month: number }>();

  // Add this property to the component class
  userExplicitlySelectedDate: boolean = false;

  /**
   * Category id requested via the `category` query param. Bound into the
   * category selectors until categories load — the selector resolves it
   * against the fetched list (falling back to the first category) and emits
   * the result through onCategoryChange, which then owns selectedCategory
   * and clears this bridge so the stale id can't resurface later (e.g. on
   * an org switch reloading the categories, or after a null emit).
   */
  requestedCategoryId: string = '';

  private static readonly VIEW_MODES: readonly BookingViewMode[] =
    ['simplified', 'day', 'week', 'day-v2', 'week-v2'];

  /**
   * Latest absolute UTC time a booking may start, derived from the selected
   * category's bookingLeadTimeMinutes. null = no limit.
   * The backend re-validates this on POST /bookings and POST /holds — the
   * pickers use this to grey out slots beyond the window.
   */
  get maxBookableUtc(): Date | null {
    const lead = this.selectedCategory?.bookingLeadTimeMinutes;
    if (lead == null) return null;
    return new Date(Date.now() + lead * 60_000);
  }

  get bookingLeadTimeMinutes(): number | null {
    return this.selectedCategory?.bookingLeadTimeMinutes ?? null;
  }

  /** Any of the four grid modes (day/week, classic or v2) — everything except Einfache Ansicht. */
  get isGridViewMode(): boolean {
    return this.viewMode !== 'simplified';
  }

  /** Wochenansicht or Wochenansicht v2 — weekly fetch/pivot logic applies. */
  get isWeekGridMode(): boolean {
    return isWeekViewMode(this.viewMode);
  }

  /** v2 modes render the dedicated app-court-lanes-grid instead of app-court-time-grid. */
  get useLanesGrid(): boolean {
    return this.viewMode === 'day-v2' || this.viewMode === 'week-v2';
  }

  /**
   * Court/duration highlight for the simplified per-court duration picker.
   * Only shown while the booking mode is 'court' — selecting a course keeps
   * the court values (D4) but visually deselects the court row, mirroring
   * the design mockup (selRow requires mode === 'court').
   */
  get simplifiedSelectedCourtId(): string | null {
    return this.bookingMode === 'court' ? this.selectedCourt?.id ?? null : null;
  }

  get simplifiedSelectedDurationMinutes(): number | null {
    return this.bookingMode === 'court' ? this.selectedDurationInMinutes : null;
  }

  /** Course-card radio highlight — only while the booking mode is 'course'. */
  get simplifiedSelectedSessionId(): string | null {
    return this.bookingMode === 'course' ? this.selectedCourseSession?.id ?? null : null;
  }

  /**
   * `showAvailableOnly` (the "Nur verfügbare anzeigen" toggle) only exists in
   * Einfache Ansicht's UI. In day/week grid mode it must always be `false`:
   * the backend drops past slots entirely instead of returning them with
   * isPast=true when availableOnly is true, which truncates "today"'s slot
   * list to start from the next bookable time instead of local midnight —
   * the grid needs the FULL day (it already renders past slots as a
   * distinct, non-clickable state) to keep every day's vertical axis
   * aligned to the same local-midnight reference.
   */
  private get effectiveAvailableOnly(): boolean {
    return this.viewMode === 'simplified' ? this.showAvailableOnly : false;
  }

  constructor(
    private courtService: CourtAvailabilityService,
    private slotHoldService: SlotHoldService,
    private cdr: ChangeDetectorRef,
    private timeUtils: TimeUtilsService,
    private toastService: ToastService,
    private errorHandlingService: ErrorHandlingService,
    private organizationService: OrganizationService,
    private authService: AuthService,
    private coursesService: CoursesService,
    private venueTimezoneService: VenueTimezoneService,
    private courtGridDataService: CourtGridDataService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Restore category/date/view from the URL before any child component
    // (category selector, calendar) initializes off this state.
    this.restoreStateFromUrl();

    // Booking-type colors (--booking-*-accent CSS vars) are applied app-wide
    // by OrganizationService on org selection - no per-route fetch needed.

    // Setup course dates fetching with automatic cancellation of stale requests.
    // When user rapidly changes months, switchMap cancels any in-flight request
    // before starting the new one, preventing race conditions where stale data
    // could overwrite current month's data.
    this.subscriptions.add(
      this.courseDatesRequest$.pipe(
        filter(() => !!this.selectedCategory),
        switchMap(({ year, month }) => {
          const startDate = new Date(year, month, 1);
          const endDate = new Date(year, month + 1, 0);
          const startDateStr = this.formatDateForApi(startDate);
          const endDateStr = this.formatDateForApi(endDate);

          return this.coursesService.getCourseDates(
            this.selectedCategory!.id,
            startDateStr,
            endDateStr
          );
        })
      ).subscribe({
        next: (response) => {
          this.datesWithCourseSessions = new Set(response.dates);
          console.log('Course dates fetched:', response.dates);
        },
        error: (error) => {
          console.error('Error fetching course dates:', error);
          // Don't show error to user - this is a non-critical feature
        }
      })
    );
  }

  /**
   * Restores category/date/view from the query params so a refresh (or a
   * shared link) doesn't lose the user's configuration.
   * - `view`: applied only if it's a known BookingViewMode.
   * - `date`: `YYYY-MM-DD` calendar date; past/invalid dates are ignored
   *   (the calendar disables past days), letting the initial load fall back
   *   to today as before.
   * - `category`: kept in requestedCategoryId until the category selector
   *   resolves it against the loaded categories (unknown ids fall back to
   *   the first category, and the URL self-corrects on the emit).
   */
  private restoreStateFromUrl(): void {
    const params = this.route.snapshot.queryParamMap;

    const view = params.get('view');
    if (view && (BookingContainerComponent.VIEW_MODES as readonly string[]).includes(view)) {
      this.viewMode = view as BookingViewMode;
    }

    const dateParam = params.get('date');
    const parsedDate = dateParam ? parseLocalDate(dateParam) : null;
    if (parsedDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (parsedDate.getTime() >= today.getTime()) {
        this.selectedDate = parsedDate;
        // Marks the date as user-chosen so refreshAvailableDurations fetches
        // for it instead of resetting to today on the initial load.
        this.userExplicitlySelectedDate = true;
      }
    }

    if (this.isWeekGridMode) {
      this.weekStartDate = mondayOf(this.selectedDate);
    }

    this.requestedCategoryId = params.get('category') ?? '';
  }

  /**
   * Mirrors the current category/date/view into the query params so the
   * configuration survives a refresh. replaceUrl keeps selection changes out
   * of the browser history (back leaves the page instead of replaying every
   * click); merge preserves unrelated params.
   *
   * While categories are still loading, requestedCategoryId carries the
   * deep-linked category — without the fallback, a date/view change in that
   * window would strip the param before the selector ever resolved it.
   */
  private syncStateToUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        category: this.selectedCategory?.id ?? (this.requestedCategoryId || null),
        date: formatLocalISODate(this.selectedDate),
        view: this.viewMode === 'simplified' ? null : this.viewMode,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private resetSelectionState(): void {
    this.selectedSlot = null;
    this.selectedSlotId = null;
    this.selectedDurationOption = null;
    this.selectedDurationInMinutes = null;
    this.selectedCourt = null;
    this.gridAvailableDurations = [];
    this.availableCourseSessions = [];
    this.bookingMode = null;
    this.selectedCourseSession = null;
    this.courtRows = [];
  }

  // When a category is selected (step 0)
  onCategoryChange(category: Category | null): void {
    console.log('Category changed to:', category?.name);

    // The selector has resolved the URL-requested id (or found no categories)
    // — the pre-load bridge is done. Clearing it keeps syncStateToUrl and the
    // selector bindings from falling back to the stale requested id.
    this.requestedCategoryId = '';
    this.selectedCategory = category;
    this.resetSelectionState();
    this.datesWithCourseSessions = new Set();
    this.syncStateToUrl();

    // Clear slots immediately when category changes and set loading state
    this.loadingSlots = true;
    this.aggregatedSlots = [];
    this.availableIntervals = [];
    this.weekDaySlots = [];
    // gridDays is a derived cache of aggregatedSlots/weekDaySlots — without this,
    // switching to "no category" (or a category refreshAvailableDurations bails
    // out of below) would leave the previous category's grid visibly on screen.
    this.rebuildGridDays();

    if (category) {
      this.refreshAvailableDurations();

      // Calendar emits monthChanged on ngOnInit, but at that point no category is
      // selected yet — fetch course dates now that one is.
      this.fetchCourseDatesForMonth(
        this.displayedCalendarMonth.year,
        this.displayedCalendarMonth.month
      );
    }
  }

  // When a date is selected (step 1)
  onDateSelected(date: Date): void {
    console.log('Date selected:', date);
    this.selectedDate = date;
    this.userExplicitlySelectedDate = true; // User explicitly selected a date

    // Reset slot selection when date changes
    this.resetSelectionState();

    if (this.isWeekGridMode) {
      // Picking any day navigates to the week that contains it.
      this.weekStartDate = mondayOf(date);
    }

    this.syncStateToUrl();

    // Fetch slots for the new date/week
    this.fetchSlotsForCurrentView();
  }

  /** Grid's day/week-nav arrows — jump one day (Tagesansicht) or one week (Wochenansicht). */
  onGridNavigatePrev(): void {
    this.navigateGrid(-1);
  }

  onGridNavigateNext(): void {
    this.navigateGrid(1);
  }

  private navigateGrid(direction: -1 | 1): void {
    this.resetSelectionState();
    if (this.isWeekGridMode) {
      const base = this.weekStartDate ?? mondayOf(this.selectedDate);
      this.weekStartDate = new Date(base.getFullYear(), base.getMonth(), base.getDate() + direction * 7);
      // Keep the highlighted calendar date inside the newly displayed week.
      this.selectedDate = this.weekStartDate;
      this.userExplicitlySelectedDate = true;
      this.syncStateToUrl();
      this.fetchWeeklySlots();
    } else {
      const d = this.selectedDate;
      this.selectedDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() + direction);
      this.userExplicitlySelectedDate = true;
      this.syncStateToUrl();
      this.fetchAvailableSlots();
    }
  }

  // When a time slot is selected (step 2)
  onSlotSelected(event: SlotSelectionEvent): void {
    const { slot, selectedDuration } = event;

    this.selectedSlot = slot;
    this.selectedSlotId = slot.startTimeUtc;

    // Explicitly provided duration becomes the auto-pick preference.
    if (selectedDuration) {
      this.selectedDurationOption = selectedDuration;
      this.selectedDurationInMinutes = selectedDuration.duration;
    }

    this.availableCourseSessions = this.composeCourseSessions(slot);
    this.buildCourtRows();
    this.autoPickSelection();
  }

  /**
   * All course sessions of a slot, joinable first — the auto-pick uses the
   * joinable group, the picker renders non-joinable ones dimmed below.
   */
  private composeCourseSessions(slot: AggregatedSlotDto): CourseSessionDto[] {
    return [
      ...(slot.courses?.joinable || []),
      ...(slot.courses?.full || []),
      ...(slot.courses?.requiresApproval || []),
      ...(slot.courses?.inviteOnly || []),
      ...(slot.courses?.membersOnly || []),
    ];
  }

  /**
   * One row per court of the selected slot (order = courts.breakdown), with
   * per-court duration feasibility derived from the single-day grid pivot
   * (see plan D1 — rebuildGridDays() also runs in simplified mode).
   */
  private buildCourtRows(): void {
    const slot = this.selectedSlot;
    const dateStr = formatLocalISODate(this.selectedDate);
    if (!slot || !dateStr || this.viewMode !== 'simplified') {
      this.courtRows = [];
      return;
    }

    const durations = this.filteredIntervals.length > 0
      ? this.filteredIntervals
      : this.availableIntervals;

    this.courtRows = slot.courts.breakdown.map(court => {
      const options = this.courtGridDataService.getAvailableDurationsForCourt(
        court.id,
        dateStr,
        slot.startTimeUtc,
        this.gridDays,
        durations,
      );
      return {
        courtId: court.id,
        courtName: court.name,
        options,
        fullyBooked: !options.some(o => o.available),
        // Distinguishes "closed" from "booked out" in the row label — the
        // court state is the one for THIS slot's breakdown (plan §6.3.4).
        blocked: isBlockedCourtState(court),
      };
    });
  }

  /**
   * Auto-picks the best court + duration for the selected slot (mirrors the
   * design mockup's pick()/selSlot()):
   * 1. previous court + previous duration when still feasible,
   * 2. previous court with its first feasible duration,
   * 3. first court with any feasible duration + its first feasible duration.
   * If no court is feasible: first joinable, not-enrolled course session
   * (mode 'course'), otherwise no selection (mode null → "Keine Auswahl").
   */
  private autoPickSelection(): void {
    const slot = this.selectedSlot;
    if (!slot) return;

    const prevCourtId = this.selectedCourt?.id ?? null;
    const prevDuration = this.selectedDurationInMinutes;
    const firstFeasible = (row: CourtRowModel) => row.options.find(o => o.available);

    let pickedRow: CourtRowModel | undefined;
    let pickedOption: CourtDurationOption | undefined;

    const prevRow = prevCourtId ? this.courtRows.find(r => r.courtId === prevCourtId) : undefined;
    if (prevRow) {
      const exact = prevDuration != null
        ? prevRow.options.find(o => o.available && o.duration === prevDuration)
        : undefined;
      const option = exact ?? firstFeasible(prevRow);
      if (option) {
        pickedRow = prevRow;
        pickedOption = option;
      }
    }

    if (!pickedOption) {
      for (const row of this.courtRows) {
        const option = firstFeasible(row);
        if (option) {
          pickedRow = row;
          pickedOption = option;
          break;
        }
      }
    }

    if (pickedRow && pickedOption) {
      this.selectedCourseSession = null;
      this.applyCourtSelection(pickedRow.courtId, pickedRow.courtName, pickedOption);
      return;
    }

    // No bookable court — fall back to the first joinable, not-enrolled course.
    this.selectedCourt = null;
    this.selectedDurationOption = null;
    this.selectedDurationInMinutes = null;
    const joinable = (slot.courses?.joinable ?? []).find(s => !s.isUserEnrolled) ?? null;
    this.selectedCourseSession = joinable;
    this.bookingMode = joinable ? 'course' : null;
  }

  /**
   * Commits a court + duration selection: maps the per-court option back to
   * the slot's booking.durations entry (same pattern as
   * onCourtGridSlotSelected) so pricing/endTime come from the backend DTO.
   */
  private applyCourtSelection(
    courtId: string,
    courtName: string,
    option: CourtDurationOption,
  ): void {
    const durationOption = this.selectedSlot?.booking?.durations?.find(
      d => d.duration === option.duration
    ) ?? null;

    this.selectedCourt = { id: courtId, name: courtName };
    this.selectedDurationOption = durationOption;
    this.selectedDurationInMinutes = durationOption ? option.duration : null;
    this.bookingMode = 'court';
  }

  /**
   * Re-validates the current selection after a refetch or filter change while
   * the selected slot survives: a still-joinable course selection is kept,
   * anything else re-runs the auto-pick (availability may have changed).
   */
  private revalidateSelection(): void {
    if (this.bookingMode === 'course' && this.selectedCourseSession) {
      const refreshed = (this.selectedSlot?.courses?.joinable ?? []).find(
        s => s.id === this.selectedCourseSession!.id && !s.isUserEnrolled
      );
      if (refreshed) {
        this.selectedCourseSession = refreshed;
        return;
      }
    }
    this.autoPickSelection();
  }

  /** Duration button clicked in a specific court row of the simplified picker. */
  onCourtDurationSelected(event: CourtDurationSelectionEvent): void {
    this.applyCourtSelection(event.courtId, event.courtName, event.option);
  }

  /**
   * Course card selected (radio) in the simplified picker — switches the CTA
   * to "Jetzt teilnehmen". Court values are kept so switching back to a court
   * duration restores the previous court selection (plan D4).
   */
  onCourseSessionSelected(session: CourseSessionDto): void {
    this.selectedCourseSession = session;
    this.bookingMode = 'course';
  }

  /**
   * Open the enrollment modal for a course session (reserve CTA in course
   * mode, or a course block clicked in the day/week grids).
   */
  onEnrollRequested(session: CourseSessionDto): void {
    this.enrollmentModalSession = session;
    this.showEnrollmentModal = true;
  }

  /** Grid views' (courseSessionClicked) binding — same enrollment modal flow. */
  onCourseSelected(session: CourseSessionDto): void {
    this.onEnrollRequested(session);
  }

  /**
   * Handle successful enrollment
   */
  onEnrollmentCompleted(): void {
    this.showEnrollmentModal = false;
    this.enrollmentModalSession = null;

    // Reset the whole selection (incl. bookingMode/courtRows) and refresh
    // slots to reflect the new enrollment.
    this.resetSelectionState();
    this.fetchAvailableSlots();
  }

  /**
   * Close the enrollment modal
   */
  closeEnrollmentModal(): void {
    this.showEnrollmentModal = false;
    this.enrollmentModalSession = null;
  }


  // Rebuild duration options from the selected category's bookingDuration config,
  // then trigger a slot fetch for the appropriate date.
  refreshAvailableDurations(): void {
    if (!this.selectedCategory?.bookingDuration) {
      this.availableIntervals = [];
      this.filteredIntervals = [];
      this.selectedIntervalFilters = [];
      this.allIntervalFiltersSelected = true;
      this.durationSelectionOptions = [];
      this.loadingIntervals = false;
      // No durations means no slot request will fire — clear the slots loading
      // flag set by onCategoryChange so the UI doesn't get stuck.
      this.loadingSlots = false;
      this.aggregatedSlots = [];
      this.rebuildGridDays();
      return;
    }

    const { minDuration, maxDuration, interval } = this.selectedCategory.bookingDuration;
    const intervals: number[] = [];
    for (let duration = minDuration; duration <= maxDuration; duration += interval) {
      intervals.push(duration);
    }

    this.availableIntervals = intervals;
    this.filteredIntervals = [...intervals];
    this.selectedIntervalFilters = intervals.map(() => true);
    this.allIntervalFiltersSelected = true;
    this.durationSelectionOptions = [...intervals];
    this.loadingIntervals = false;

    if (this.isWeekGridMode && !this.weekStartDate) {
      this.weekStartDate = mondayOf(this.selectedDate);
    }

    const isInitialLoad = !this.userExplicitlySelectedDate && !this.aggregatedSlots.length;
    if (isInitialLoad) {
      this.selectedDate = new Date();
      this.fetchSlotsForCurrentView();
    } else if (this.userExplicitlySelectedDate && this.selectedDate) {
      this.fetchSlotsForCurrentView();
    }
  }

  // Fetch available slots for booking. Optionally restores scroll position after loading.
  fetchAvailableSlots(scrollY?: number): void {
    if (!this.selectedCategory || !this.selectedDate) {
      console.log('Missing prerequisites for fetching slots');
      return;
    }

    // Use filteredIntervals if available, otherwise use all available intervals
    const durations = this.filteredIntervals.length > 0 ?
      this.filteredIntervals : this.availableIntervals;

    if (!durations.length) {
      console.log('No durations available for fetching slots');
      return;
    }

    const dateString = this.timeUtils.formatDateForApi(this.selectedDate);
    const categoryId = this.selectedCategory.id;

    // Route is guarded by authGuard, so a user is always present here.
    const organizationId = this.organizationService.selectedOrganizationId() ?? undefined;

    // Pass userId to check enrollment status for course sessions
    const userId = this.authService.currentUserSignal()?.userId;

    this.loadingSlots = true;
    // Store the subscription
    const subscription = this.courtService.getAggregatedAvailableSlots(
      dateString,
      durations,
      categoryId,
      undefined, // membershipType
      organizationId,
      userId,
      this.effectiveAvailableOnly // availableOnly - user toggle, but never in grid mode (see getter)
    )
      .pipe(finalize(() => {
        this.loadingSlots = false;
        if (scrollY !== undefined) {
          setTimeout(() => window.scrollTo(0, scrollY), 0);
        }
      }))
      .subscribe({
        next: (response) => {
          // Set venue timezone from API response for proper time display
          if (response.timezone) {
            this.venueTimezoneService.setTimezone(response.timezone);
          }

          this.aggregatedSlots = response.slots;
          this.intervalMinutes = response.intervalMinutes;

          // Validate that the previously selected slot still exists in the new results
          const refreshedSlot = this.selectedSlotId
            ? this.aggregatedSlots.find(s => s.startTimeUtc === this.selectedSlotId) ?? null
            : null;
          if (this.selectedSlotId && !refreshedSlot) {
            this.resetSelectionState();
          }

          this.rebuildGridDays();

          // The slot survived the refetch, but its availability may have
          // changed — rebind to the fresh DTO, rebuild the court rows and
          // re-validate the auto-picked selection (plan D4).
          if (refreshedSlot && this.viewMode === 'simplified') {
            this.selectedSlot = refreshedSlot;
            this.availableCourseSessions = this.composeCourseSessions(refreshedSlot);
            this.buildCourtRows();
            this.revalidateSelection();
          }
        },
        error: (error) => {
          this.toastService.error(this.errorHandlingService.getGeneralErrorMessage(error));
        }
      });

    // Add new subscription to the list
    this.subscriptions.add(subscription);
  }

  /** Routes to the day or week fetch depending on the active view mode. */
  private fetchSlotsForCurrentView(scrollY?: number): void {
    if (this.isWeekGridMode) {
      this.fetchWeeklySlots();
    } else {
      this.fetchAvailableSlots(scrollY);
    }
  }

  // Fetch 7 days of aggregated slots at once for Wochenansicht.
  fetchWeeklySlots(): void {
    if (!this.selectedCategory || !this.weekStartDate) {
      console.log('Missing prerequisites for fetching weekly slots');
      return;
    }

    const durations = this.filteredIntervals.length ?
      this.filteredIntervals : this.availableIntervals;

    if (!durations.length) {
      console.log('No durations available for fetching weekly slots');
      return;
    }

    const startDateString = this.timeUtils.formatDateForApi(this.weekStartDate);
    const categoryId = this.selectedCategory.id;
    const organizationId = this.organizationService.selectedOrganizationId() ?? undefined;
    const userId = this.authService.currentUserSignal()?.userId;

    this.loadingSlots = true;
    const subscription = this.courtService.getWeeklyAggregatedSlots(
      startDateString,
      durations,
      categoryId,
      undefined,
      organizationId,
      userId,
      this.effectiveAvailableOnly
    )
      .pipe(finalize(() => this.loadingSlots = false))
      .subscribe({
        next: (response) => {
          if (response.timezone) {
            this.venueTimezoneService.setTimezone(response.timezone);
          }

          this.weekDaySlots = response.days;
          this.intervalMinutes = response.intervalMinutes;

          if (this.selectedSlotId) {
            const stillExists = this.weekDaySlots.some(
              d => d.slots.some(s => s.startTimeUtc === this.selectedSlotId)
            );
            if (!stillExists) {
              this.resetSelectionState();
            }
          }

          this.rebuildGridDays();
        },
        error: (error) => {
          this.toastService.error(this.errorHandlingService.getGeneralErrorMessage(error));
        }
      });

    this.subscriptions.add(subscription);
  }

  /**
   * Rebuilds the pivoted grid model consumed by app-court-time-grid for the
   * active view mode. Simplified mode builds the same single-day pivot as
   * Tagesansicht — it feeds the per-court duration feasibility of the
   * simplified court rows (getAvailableDurationsForCourt, plan D1).
   */
  private rebuildGridDays(): void {
    if (this.viewMode === 'simplified' || isDayViewMode(this.viewMode)) {
      const dateStr = formatLocalISODate(this.selectedDate);
      this.gridDays = dateStr
        ? this.courtGridDataService.buildWeekGrid([{ date: dateStr, slots: this.aggregatedSlots }], this.intervalMinutes)
        : [];
    } else if (isWeekViewMode(this.viewMode)) {
      this.gridDays = this.courtGridDataService.buildWeekGrid(this.weekDaySlots, this.intervalMinutes);
    }
  }

  /**
   * Helper for API date formatting that preserves the selected day regardless of timezone
   */
  private formatDateForApi(date: Date): string {
    return this.timeUtils.formatDateForApi(date);
  }

  /**
   * Handle calendar month change - fetch course dates for the new month
   */
  onCalendarMonthChanged(event: { year: number; month: number }): void {
    // Track the displayed month for use when category changes
    this.displayedCalendarMonth = event;

    if (!this.selectedCategory) {
      return;
    }

    this.fetchCourseDatesForMonth(event.year, event.month);
  }

  /**
   * Fetch dates with available course sessions for a given month.
   * Emits to courseDatesRequest$ which uses switchMap to automatically
   * cancel any in-flight request when a new one is triggered.
   */
  private fetchCourseDatesForMonth(year: number, month: number): void {
    if (!this.selectedCategory) {
      return;
    }
    this.courseDatesRequest$.next({ year, month });
  }

  // Add this method to handle reservation
  onReserveClicked(slot: AggregatedSlotDto): void {
    if (!slot || !this.selectedCategory) {
      this.toastService.error('Kein Zeitfenster oder keine Kategorie für die Reservierung ausgewählt');
      return;
    }

    if (!this.selectedDurationInMinutes) {
      this.toastService.warning('Bitte wählen Sie zuerst eine Dauer aus');
      return;
    }

    // Every reserve path (auto-pick, court-row click, grid selection) sets
    // selectedCourt before enabling the CTA — this guard keeps a broken path
    // from silently degrading the hold request to an unspecified court.
    if (!this.selectedCourt) {
      this.toastService.warning('Bitte wählen Sie zuerst einen Platz aus');
      return;
    }

    const durations = slot.booking?.durations || [];
    const selectedDuration = durations.find(d => d.duration === this.selectedDurationInMinutes);
    if (!selectedDuration) {
      this.toastService.error('Die gewählte Dauer ist nicht verfügbar. Bitte wähle eine andere.');
      return;
    }

    // Keep original ISO time strings for API communication
    // but format for display where needed
    const bookingRequestDto: CourtSlotBookingRequestDto = {
      court: {
        id: this.selectedCourt.id,
        name: this.selectedCourt.name,
      },
      category: {
        id: this.selectedCategory.id,
        name: this.selectedCategory.name,
        organizationId: this.selectedCategory.organizationId,
        minPlayers: this.selectedCategory.minPlayers,
        maxPlayers: this.selectedCategory.maxPlayers,
        pricingPerPlayer: this.selectedCategory.pricingPerPlayer,
      },
      startTime: slot.startTimeUtc, // Keep as ISO string for API
      endTime: selectedDuration.endTime,
      durationMinutes: this.selectedDurationInMinutes,
      price: selectedDuration.price,
      date: this.selectedDate,
      id: slot.startTimeUtc,
      clientTimeZone: this.timeUtils.getClientTimeZone(),
    };

    // Set modal flag before hold operation
    this.showBookingModal = true;
    
    // Ensure change detection runs after setting the flag
    this.cdr.detectChanges();

    // Then invoke the service
    this.slotHoldService.holdSlot(bookingRequestDto);
  }
  
  // Add this method to handle closing the modal
  onModalClosed(): void {
    this.showBookingModal = false;
  }

  onBookingConfirmed(): void {
    // Clear local hold state — no API call needed since the hold was consumed by the booking
    this.slotHoldService.clearLocalHoldState();

    // Reset selection state but keep the modal open for the confirmation screen
    this.resetSelectionState();

    // Refresh slots to reflect the newly booked court as unavailable
    this.fetchAvailableSlots();
  }

  // Add this method to clean up resources
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.unsubscribe();

    // Complete the course dates request subject
    this.courseDatesRequest$.complete();

    // Clear any references that might prevent garbage collection
    this.selectedSlot = null;
    this.aggregatedSlots = [];
    this.availableCourts = [];
    this.availableSlots = { courts: [] };
    this.gridDays = [];
    this.weekDaySlots = [];
  }

  // Update the onDurationFilterChange method
  onDurationFilterChange(result: {
    selectedIntervals: number[],
    selectedFilters: boolean[],
    allSelected: boolean
  }): void {
    this.filteredIntervals = result.selectedIntervals;
    this.selectedIntervalFilters = result.selectedFilters;
    this.allIntervalFiltersSelected = result.allSelected;

    // Clear duration selection if the selected duration is no longer in the
    // filtered set — the refetch's revalidation then auto-picks a fallback
    // per the auto-pick rules (mirrors the mockup's toggleFilter()).
    if (this.selectedDurationInMinutes && !this.filteredIntervals.includes(this.selectedDurationInMinutes)) {
      this.selectedDurationOption = null;
      this.selectedDurationInMinutes = null;
    }

    // If no durations selected, don't fetch slots
    if (this.filteredIntervals.length === 0) {
      this.aggregatedSlots = [];
      this.rebuildGridDays();
      this.resetSelectionState();
      return;
    }

    this.durationSelectionOptions = this.filteredIntervals;

    // Fetch slots for ALL selected durations. The next-handler re-validates
    // the surviving selection (buildCourtRows + auto-pick) once fresh
    // availability arrives.
    this.fetchSlotsForCurrentView();
  }

  // Add this method to filter available slots
  filterAvailableSlots(): void {
    if (this.filteredIntervals.length === 0 ||
        this.filteredIntervals.length === this.availableIntervals.length) {
      // Show all slots if no filters are applied or all are selected
      // You might need to restore from a cached original list if you're filtering in-place
    } else {
      // Filter the slots based on selected durations
      // This depends on your data structure and might need adjustment
    }
  }

  // View mode toggle between simplified, Tagesansicht(/v2), and Wochenansicht(/v2).
  // The v2 modes are pure visual variants of their classic counterpart — they
  // share the same dataset, so switching week <-> week-v2 or day <-> day-v2
  // never refetches, it only re-pivots.
  onViewModeChanged(mode: BookingViewMode): void {
    const prevMode = this.viewMode;
    const prevEffectiveAvailableOnly = this.effectiveAvailableOnly;
    const wasWeekMode = isWeekViewMode(prevMode);
    this.viewMode = mode;
    this.resetSelectionState();
    this.syncStateToUrl();

    if (isWeekViewMode(mode)) {
      if (wasWeekMode) {
        // week <-> week-v2: weekDaySlots for the displayed week are already
        // loaded — visual variant change only.
        this.rebuildGridDays();
      } else {
        this.weekStartDate = mondayOf(this.selectedDate);
        this.fetchWeeklySlots();
      }
    } else if (wasWeekMode) {
      // Coming back from a week mode — the single-day slots for the currently
      // selected date haven't been fetched yet.
      this.fetchAvailableSlots();
    } else if (prevEffectiveAvailableOnly !== this.effectiveAvailableOnly) {
      // simplified <-> day(/v2) while "Nur verfügbare anzeigen" is active: the
      // cached slots were fetched with a different availableOnly filter (the
      // backend drops past/unavailable slots when it's true), so re-pivoting
      // them would leave the day grid without its past top section (or the
      // simplified list ignoring the toggle). Refetch with the new mode's
      // effective filter instead.
      this.fetchAvailableSlots();
    } else {
      // simplified <-> day(/v2) or day <-> day-v2 with matching filter:
      // aggregatedSlots is already loaded and shared; just re-pivot it for
      // the grid if entering a day mode.
      this.rebuildGridDays();
    }
  }

  // Handle court + day + slot selection from the day/week grid.
  onCourtGridSlotSelected(event: CourtGridSelectionEvent): void {
    this.selectedCourt = { id: event.courtId, name: event.courtName };

    // Reconcile the page's selected date against the grid's authoritative
    // selection date — a week-view drag may have moved the selection onto a
    // different day-column without any new fetch (all 7 days are already
    // loaded). This must NOT retrigger fetchAvailableSlots(); it's a display
    // reconciliation only, unconditional by construction (see plan §7).
    const parsedDate = parseLocalDate(event.dateStr);
    if (parsedDate) {
      this.selectedDate = parsedDate;
      this.syncStateToUrl();
    }

    this.selectedSlot = event.aggregatedSlot;
    this.selectedSlotId = event.startTimeUtc;
    this.gridAvailableDurations = event.availableDurations;

    const durationOption = event.aggregatedSlot.booking?.durations?.find(
      d => d.duration === event.durationMinutes
    );

    if (durationOption) {
      this.selectedDurationOption = durationOption;
      this.selectedDurationInMinutes = event.durationMinutes;
    }
  }

  // Handle duration change from grid selection panel
  onGridDurationChanged(durationMinutes: number): void {
    if (!this.selectedSlot) return;

    const durationOption = this.selectedSlot.booking?.durations?.find(
      d => d.duration === durationMinutes
    );

    if (durationOption) {
      this.selectedDurationOption = durationOption;
      this.selectedDurationInMinutes = durationMinutes;
    }
  }

  // Handle reserve from grid selection panel
  onGridReserveClicked(): void {
    if (this.selectedSlot) {
      this.onReserveClicked(this.selectedSlot);
    }
  }

  // Handle toggle for showing only available slots
  onAvailableOnlyToggle(): void {
    console.log('Available only filter changed to:', this.showAvailableOnly);

    // Store current scroll position before fetch to prevent layout jump
    const scrollY = window.scrollY;

    // Fetch slots and restore scroll position after data loads
    this.fetchSlotsForCurrentView(scrollY);
  }

}

export interface CourtSlotBookingRequestDto {
  court: {
    id: string;
    name: string;
  }
  startTime: string; // ISO string
  endTime: string; // ISO string
  durationMinutes: number
  price: number;
  date: Date;
  id: string; // Unique identifier for the time '6:30-30'
  category: {
    id: string;
    name: string;
    organizationId?: string; // Organization ID for voucher filtering
    minPlayers?: number;
    maxPlayers?: number;
    pricingPerPlayer?: boolean; // Per-player pricing mode
  }
  clientTimeZone?: string; // Add client time zone
  /** Selected player IDs for multi-player booking */
  playerUserIds?: string[];
}
